import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { ChevronDown, Users } from 'lucide-react';
import { initials } from '@/lib/utils';
import { Button, Checkbox, Input, Popover, PopoverContent, PopoverTrigger } from '@/components/ui/primitives';
import { avatarStyle } from './calendar-colors';
import type { CalendarProvider } from './calendar-types';
import { DEFAULT_PROVIDER_COUNT } from './provider-selection';

export function ProviderPicker({
  providers,
  selected,
  onChange,
  onReset,
}: {
  providers: CalendarProvider[];
  selected: string[];
  onChange: (ids: string[]) => void;
  onReset: () => void;
}) {
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();
  const shown = term
    ? providers.filter((provider) => `${provider.name} ${provider.designation ?? ''} ${provider.specialty ?? ''}`.toLowerCase().includes(term))
    : providers;
  const chosen = new Set(selected);

  const toggle = (id: string, on: boolean) => {
    const next = on ? [...selected, id] : selected.filter((item) => item !== id);
    onChange(providers.filter((provider) => next.includes(provider.id)).map((provider) => provider.id));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Users className="h-4 w-4" aria-hidden />
          {tr('opd', 'x_of_x_providers', { shown: selected.length, total: providers.length })}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-2">
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={tr('opd', 'search_providers')} autoFocus />
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {shown.map((provider) => (
            <label key={provider.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent">
              <Checkbox checked={chosen.has(provider.id)} onCheckedChange={(on) => toggle(provider.id, on)} />
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[10px] font-semibold" style={avatarStyle(provider.color)} aria-hidden>
                {initials(provider.name)}
              </span>
              <span className="min-w-0 flex-1 truncate">
                {provider.name}
                {provider.designation ? <span className="text-xs text-muted-foreground"> · {provider.designation}</span> : null}
              </span>
            </label>
          ))}
          {shown.length === 0 && <p className="px-1 py-3 text-center text-xs text-muted-foreground">{tr('opd', 'no_providers_match')}</p>}
        </div>
        <div className="flex justify-between gap-2 border-t pt-2">
          <Button variant="ghost" size="sm" onClick={onReset}>
            {tr('opd', 'show_first_x', { count: DEFAULT_PROVIDER_COUNT })}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onChange(providers.map((provider) => provider.id))}>
            {tr('opd', 'show_all')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
